/**
 * EGM Details opens the drawer to 75% of the screen, leaving the top 25% of the
 * map visible. That strip is further occluded by the casino header + All /
 * Favorites / Playlist pills. Mapbox centres a flyTo target in the midpoint of
 * the *unpadded* viewport, so we reserve:
 *   - bottom padding ≈ 75% of map height (drawer)
 *   - top padding = measured height of the header/pills overlay over the map
 * so the pin lands in the clear band between the pills and the drawer.
 *
 *   clearTop = topPadding
 *   clearBottom = mapHeight - bottomPadding
 *   visualYFromTop = topPadding + (mapHeight - topPadding - bottomPadding) / 2
 *                 = centre of the truly visible strip
 */
export const DETAIL_DRAWER_HEIGHT_FRACTION = 0.75;
export const DETAIL_VISIBLE_MAP_FRACTION = 1 - DETAIL_DRAWER_HEIGHT_FRACTION;

/** Minimum clear band (px) so Mapbox can still place a centre between paddings. */
export const DETAIL_FOCUS_MIN_CLEAR_PX = 40;

/** Bottom camera padding that covers the 75% EGM Details drawer. */
export function detailFocusBottomPadding(mapHeight: number): number {
  if (!Number.isFinite(mapHeight) || mapHeight <= 0) return 0;
  return Math.round(mapHeight * DETAIL_DRAWER_HEIGHT_FRACTION);
}

/**
 * Distance from the map container top to the bottom of the Explore shortcut
 * pills (All / Favorites / Playlist). Uses live layout measurement — the bar's
 * 24px backdrop-filter padding is excluded by reading the children, not the
 * bar's border box.
 */
export function measureDetailFocusTopPadding(mapContainer: HTMLElement): number {
  if (typeof document === 'undefined') return 0;
  const bar = document.querySelector('.mapstats .shortcut-bar');
  if (!(bar instanceof HTMLElement)) return 0;

  const mapTop = mapContainer.getBoundingClientRect().top;
  let overlayBottom = mapTop;
  const { children } = bar;
  if (children.length > 0) {
    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      if (child instanceof HTMLElement) {
        overlayBottom = Math.max(overlayBottom, child.getBoundingClientRect().bottom);
      }
    }
  } else {
    const padBottom = parseFloat(getComputedStyle(bar).paddingBottom) || 0;
    overlayBottom = bar.getBoundingClientRect().bottom - padBottom;
  }

  return Math.max(0, Math.round(overlayBottom - mapTop));
}

/**
 * Camera padding for the EGM Details focus fly: top = header/pills overlay,
 * bottom = 75% drawer. Clamps top so a minimum clear band remains.
 */
export function detailFocusCameraPadding(
  mapHeight: number,
  overlayTopPx: number
): { top: number; bottom: number } {
  const bottom = detailFocusBottomPadding(mapHeight);
  if (!Number.isFinite(mapHeight) || mapHeight <= 0) {
    return { top: 0, bottom: 0 };
  }
  let top = Math.max(0, Math.round(overlayTopPx));
  if (top + bottom > mapHeight - DETAIL_FOCUS_MIN_CLEAR_PX) {
    top = Math.max(0, mapHeight - bottom - DETAIL_FOCUS_MIN_CLEAR_PX);
  }
  return { top, bottom };
}
